import { Trendbase, ChartOffsets } from "../trend/trendbase.js";
import { Pen } from "../trend/pen.js";
import { getFormattedValue, exportCSVfile, generateFileName } from "../../lib/utils.js";
import { UiModal } from "../common/modal/modal.js";
import { Legend } from "../trend/legend/legend.js";
import i18next from "i18next";
import styles from '../trend/trend.css'
import { TrendTable } from './table/table.js'
/**
 * @class MSChart
 * @extends Trendbase
 * @classdesc График XY
 * */

const tableIcon = '<svg width=\\"15\\" height=\\"13\\" viewBox=\\"0 0 15 13\\" xmlns=\\"http://www.w3.org/2000/svg\\"><path fill-rule=\\"evenodd\\" clip-rule=\\"evenodd\\" d=\\"M1 0H15V1V4V5V8V9V12V13H14H5H4H0V12V9V8V5V4V1V0H1ZM1 1L1 4H4V1L1 1ZM1 5L1 8H4L4 5H1ZM1 9L0.999999 12H4V9H1ZM5 1L14 1V4H5V1ZM5 5H14L14 8H5L5 5ZM5 9H14V12H5V9Z\\"/></svg>'

export class MSChart extends Trendbase {
    static get observedAttributes() {
        return super.observedAttributes.concat(["miny", "maxy", "yaxislabel"]);
    }
    constructor() {
        super();
        this.checkComponent('ui-modal', UiModal)
        const xdatazoomWidth = this.attributes.width.value - ChartOffsets.left - ChartOffsets.right;
        const shadowRoot = this.attachShadow({ mode: "open" });
        this.isChartXY = true;
        shadowRoot.innerHTML = `
            <style>
                ${styles}
            </style>
            <div class="trend">
                <div class="trend__container" id="trend__container">
                    <ms-toolbar
                        id='toolbar'
                        class="trend__toolbar"
                        style="position: relative;"
                        buttons='[
                            {"method": "", "class": ["hmi-j-tools"], "tooltip": ["${i18next.t('trend_toolbar_table')}", "${i18next.t('trend_toolbar_table')}"], "svg": "${tableIcon}", "type": "toggle", "id": "tool-table-btn"},
                            {"method": "ShowLegend", "class": ["hmi-j-trend_legend", "hmi-j-trend_legend"], "tooltip": ["${i18next.t('trend_toolbar_minilegenda')}", "${i18next.t('trend_toolbar_minilegenda')}"], "type": "toggle"},
                            {"method": "DownloadCsv", "class": "hmi-j-export", "tooltip": "${i18next.t('trend_toolbar_export')}", "type": "button"},
                            {"method": "Print", "class": "hmi-j-print", "tooltip": "${i18next.t("trend_toolbar_print")}", "type": "button"},
                            {"method": "SaveImageAs", "class": "hmi-j-print_screen", "tooltip": "${i18next.t('trend_toolbar_exportGraph')}", "type": "button"}
                        ]'
                    ></ms-toolbar>
                    <div id="chart-wrapper" class="trend__chart-wrapper">
                        <ms-datazoom
                            id="ydatazoom"
                            class="trend__ydatazoom"
                            height="${this.attributes.height.value}"
                            datazoomindex="1"
                            format="f0"
                            width="20"
                            min="0"
                            max="100"
                            minvalue="0"
                            maxvalue="100"
                            foreborderwidth="2"
                            orientation="1"
                        ></ms-datazoom>
                        <div id="chart" class="trend__chart"></div>
                         <ms-datazoom
                        id="datazoom"
                        class="trend__xdatazoom"
                        style="position: relative;"
                        datazoomindex="0"
                        format="f0"
                        width="${xdatazoomWidth}"
                        height="20"
                        orientation="0"
                        foreborderwidth="2"
                    ></ms-datazoom>
                    </div>
                </div>
            </div>
        `;
        this.setParts(shadowRoot);
        this.createTrend();
        //базовый отступ grid для 1 шкалы Y
        this.echarts.setOption({ grid: { left: 45 } });
        let penlimits = this.getPenlimits();
        penlimits.max = +this.attributes.maxy.value;
        penlimits.min = +this.attributes.miny.value;
        this._penlimits = penlimits;
        this.createMarkAreaOption(penlimits);
        this.createDataZoom();
        this.initLegend();
        this.initTrendTable();
        this.msdatazoom.echarts = this.echarts;
        this.msdatazoom.updateForDatazoom = (start, end) => {
            this.echarts &&
                this.echarts.dispatchAction({
                    type: "dataZoom",
                    dataZoomIndex: 0,
                    startValue: start,
                    endValue: end,
                });
        };
        this.msydatazoom.echarts = this.echarts;
        this.msydatazoom.min = this.miny;
        this.msydatazoom.max = this.maxy;
        this.msydatazoom.updateForDatazoom = (start, end) => {
            this.echarts &&
                this.echarts.dispatchAction({
                    type: "dataZoom",
                    dataZoomIndex: 1,
                    startValue: start,
                    endValue: end,
                });
        };
        this.echarts.setOption({
            tooltip: {
                trigger: 'axis',
                formatter: (params) => {
                    if (!params || !params.length) return '';

                    const xLabel = this._xaxislabel || 'x';
                    const yLabel = this._yaxislabel || 'y';

                    let result = '';
                    params.forEach((param) => {
                        const xValue = getFormattedValue(this._xformat, param.data[0], null, null, true);
                        const yValue = getFormattedValue(this.pens[param.seriesIndex]?._yformat || this._yformat, param.data[1], null, null, true);
                        const seriesName = param.seriesName;
                        const color = param.color;

                        result += `<div><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background-color:${color};margin-right:5px;"></span>${seriesName} ${xLabel}: ${xValue} ${yLabel}: ${yValue}</div>`;
                    });
                    return result;
                },
                axisPointer: {
                    type: 'cross',
                    snap: true,
                    crossStyle: {
                        color: '#000000',
                        opacity: 0.5,
                        width: 1,
                    },
                    label: {
                        show: true,
                        backgroundColor: '#004E52',
                    },
                },
            },
            xAxis: {
                nameLocation: 'middle',
                type: "value",
                scale: true,
                axisLabel: {
                    color: "#000000",
                    formatter: (value, index) => {
                        return getFormattedValue(
                            this._xformat,
                            value,
                            null,
                            null,
                            true
                        );
                    },
                },
                axisPointer: {
                    show: true,
                    snap: true,
                    label: {
                        show: false,
                        formatter: (value, index) => {
                            return getFormattedValue(
                                this._xformat,
                                value.value,
                                null,
                                null,
                                true
                            );
                        },
                    },
                },
            },
            yAxis: {
                type: "value",
                scale: true,
                axisLabel: {
                    formatter: (value, index) => {
                        return getFormattedValue(
                            this._yformat,
                            value,
                            null,
                            null,
                            true
                        );
                    },
                },
                axisPointer: {
                    show: true,
                    snap: true,
                    label: {
                        show: false,
                        formatter: (value, index) => {
                            return getFormattedValue(
                                this._yformat,
                                value.value,
                                null,
                                null,
                                true
                            );
                        },
                    },
                },
            },
        });
        this.echarts.on("datazoom", () => {
            this.msdatazoom.minvalue = this.datazoom.startValue;
            this.msdatazoom.maxvalue = this.datazoom.endValue;
            this.msydatazoom.minvalue = this.ydatazoom.startValue;
            this.msydatazoom.maxvalue = this.ydatazoom.endValue;
        });
        this.toolbar.context = this;

        this.updateMsydatazoomHeight();
    }

    initLegend() {
        this.legend = new Legend(this);
    }

    initTrendTable(){
        const table = this.toolbar.shadowRoot.querySelector("#tool-table-btn")
        this.trendTable = new TrendTable(this, table)
    }

    setScope() {
        const curPen = this.pens[this.currentPen]
        const pen = curPen?.isvisible ? curPen : this.pens.find(pen => pen.isvisible)
        this.currentPen = this.currentPen === pen?.idx ? this.currentPen : pen?.idx
    }

    updateMsydatazoomHeight() {
        const msdatazoomHeight = this.msdatazoom.isvisible
            ? this.msdatazoom.height
            : 0;
        const { top, bottom } = ChartOffsets;
        this.msydatazoom.height =
            this.attributes.height.value -
            parseFloat(this.toolbar.height) -
            msdatazoomHeight -
            top -
            bottom;
    }

    changePenAttribute(attr, value) {
        let index = +attr.split(".")[1];
        if (Number.isNaN(index) || !this.pens[index]) {
            return;
        }
        let parameter = attr.split(".")[2];
        this.pens[index][parameter.toLowerCase()] = value;
        this.echarts.setOption({
            series: this.pens.series,
            yAxis: this.pens.yaxises,
        });
    }

    createDataZoom() {
        this.echarts.setOption({
            dataZoom: [
                {
                    type: "inside",
                    filterMode: "none",
                    xAxisIndex: 0,
                },
                {
                    type: "inside",
                    filterMode: "none",
                    yAxisIndex: 0,
                },
            ],
        });

        const [dataZoom, yDataZoom] = this.echarts.getModel().option.dataZoom;
        this.datazoom = dataZoom;
        this.ydatazoom = yDataZoom;
    }
    addDataSource() { }
    dummy() {
        return;
    }
    onStringPenChanged(path, value) {
        let i = path.split(".")[1];
        this.pens.series[i].data = value;
        this.echarts.setOption({
            series: this.pens.series,
        });
        this.msdatazoom.min = this.datazoom.startValue;
        this.msdatazoom.max = this.datazoom.endValue;
        this.msdatazoom.minvalue = this.datazoom.startValue;
        this.msdatazoom.maxvalue = this.datazoom.endValue;
        this.trendTable?.render();
    }
    createYAxisesOption() {
        if (this.attributes.data.value === "") {
            this.pens.yaxises = {};
            return;
        }
        this.pens.yaxises = {
            type: "value",
            splitLine: {
                lineStyle: {
                    color: this.attributes.gridfill.value,
                },
                axisLine: {
                    show: true,
                    lineStyle: {
                        width: +this.attributes.axisthicknessy.value,
                    },
                },
                axisTick: {
                    show: true,
                    lineStyle: {
                        width: +this.attributes.tickheighty.value,
                        color: this.attributes.tickcolory.value,
                        length: +this.attributes.tickwidthy.value,
                    },
                },
            },
            min: +this.attributes.miny.value,
            max: +this.attributes.maxy.value,
        };
    }
    createSeriesOption() {
        this.pens = [];
        this.pens.series = [];
        try {
            const pens = JSON.parse(this.attributes.data.value);
            let index = 0;
            for (let penname in pens) {
                let name = pens[penname].name;
                pens[penname].penname = name;
                this.pens.push(new Pen(this, pens[penname]));
                this.pens[index].updateYAxisesOffset = this.dummy;
                this.pens[index].updateSeriesAxises = this.dummy;
                this.pens[index].broadcast = this.dummy;
                this.pens[index].source = name;
                this.pens[index]._echarts = this.echarts;
                this.pens[index].minilegend = this.legend;
                this.pens[index].idx = index;
                this.pens.series.push(this.pens[index].serie);
                index++;
            }
        } catch (e) {
            $ns.add({
                type: "info",
                time: new Date().toLocaleString(),
                title: i18next.t("chart_XY_chart_is_empty"),
                text: String.format(i18next.t("chartXY_missing_pens"), this.id ,this.attributes.elname?.value ?? ""),
            });
        }
        this.trendTable?.render();
    }
    applyMark(markarea, markline) {
        if (markarea.length > 0)
            this.echarts.setOption({
                series: {
                    markLine: { data: markline },
                    markArea: { silent: true, data: markarea },
                },
            });
    }

    getYAxis() {
        return this.pens.yaxises.length ? this.pens.yaxises : {}
    }

    _generateArrayForTable() {
        const resultTable = [["", "x", "y"]];

        this.pens.forEach(pen => {
            const data = pen.serie.data || [];
            data.forEach((point, index) => {
                const x = getFormattedValue(this._xformat, point[0], null, null, true);
                const y = getFormattedValue(pen._yformat || this._yformat, point[1], null, null, true);

                if (index === 0) {
                    resultTable.push([pen.name, x, y]);
                } else {
                    resultTable.push(["", x, y]);
                }
            });
        });

        return resultTable;
    }


    DownloadCsv() {
        const fileName =
            generateFileName(
                this.exportnameformat,
                this.elname,
                this.parentElement?.getAttribute("objectid")
            ) + ".csv";

        if ($pm.hasPermissions) {
            const options = {
                cancel: () => {},
                customText: window.savePath
                    ? `${i18next.t('chartcolumn_export_data')} : ${this.elname} (${window.savePath}${fileName})${window.saveDeviceDesc}`
                    : `${i18next.t('chartcolumn_export_data')} : ${this.elname}`,
            };
            $pm.resolveControl(
                this,
                "TrendSave",
                this._DownloadCsv.bind(this, fileName),
                options
            );
        } else this._DownloadCsv(fileName);
    }

    _DownloadCsv(fileName) {
        const resultTable = this._generateArrayForTable();
        let res = "";
        for (const iterator of resultTable) {
            res += `${iterator.join(";")}\n`;
        }
        exportCSVfile(res, fileName, true);
    }

    get height() {
        return super.height;
    }
    set height(value) {
        if (value === undefined) return
        super.height = value;
        setTimeout(() => {
            this.updateHeight(value)
            this.updateMsydatazoomHeight();
        }, 230) // таймер нужен, чтобы компонент успел обновить высоту
    }

    set textcolor(value) {
        value = this.tryToGetUserColor(value);
        this.echarts.setOption({ title: { textStyle: { color: value } } });
        this.echarts.setOption({ xAxis: { axisLabel: { color: value } } });
        this.echarts.setOption({ xAxis: { nameTextStyle: { color: value } } });
        this.echarts.setOption({ yAxis: { axisLabel: { color: value } } });
    }
    get miny() {
        return this._miny;
    }
    set miny(value) {
        this._miny = +value;
        this.msydatazoom.min = this._miny;
        this.msydatazoom.minvalue = this._miny;
        this.pens.yaxises.min = this._miny;
        this._penlimits = this._penlimits || {};
        this._penlimits.min = this._miny;
        this.createMarkAreaOption(this._penlimits);
        this.echarts.setOption({ yAxis: this.pens.yaxises });
    }
    get maxy() {
        return this._maxy;
    }
    set maxy(value) {
        this._maxy = +value;
        this.msydatazoom.max = this._maxy;
        this.msydatazoom.maxvalue = this._maxy;
        this.pens.yaxises.max = this._maxy;
        this._penlimits = this._penlimits || {};
        this._penlimits.max = this._maxy;
        this.createMarkAreaOption(this._penlimits);
        this.echarts.setOption({ yAxis: this.pens.yaxises });
    }

    get yaxislabel(){
        return this._yaxislabel
    }

    set yaxislabel(v){
        this._yaxislabel = v
        this.pens.yaxises.name = v
        this.echarts.setOption({ yAxis: this.pens.yaxises });
    }

    setCurrentPen(){

    }

    /**
     * Обновить перья и оси в echarts
     * */
    updateSeriesAxises() {
        //этот метод обязан быть, так как вызывается из Pen
    }
}
